import { useEffect, useRef, useState } from "react";
import { useNavigate } from "react-router-dom";
import { homeSlides } from "@/utils/catalog";

export default function HeroCarousel() {
  const navigate = useNavigate();
  const [index, setIndex] = useState(0);
  const touchStartX = useRef<number | null>(null);

  useEffect(() => {
    const timer = window.setInterval(() => {
      setIndex((current) => (current + 1) % homeSlides.length);
    }, 6000);
    return () => window.clearInterval(timer);
  }, []);

  const goTo = (next: number) => {
    setIndex((next + homeSlides.length) % homeSlides.length);
  };

  const handleTouchStart = (event: React.TouchEvent) => {
    touchStartX.current = event.touches[0].clientX;
  };

  const handleTouchEnd = (event: React.TouchEvent) => {
    if (touchStartX.current === null) return;
    const delta = event.changedTouches[0].clientX - touchStartX.current;
    if (Math.abs(delta) > 50) {
      goTo(delta < 0 ? index + 1 : index - 1);
    }
    touchStartX.current = null;
  };

  const slide = homeSlides[index];

  return (
    <section
      className="relative w-full overflow-hidden bg-primary-950"
      onTouchStart={handleTouchStart}
      onTouchEnd={handleTouchEnd}
    >
      <div className="relative h-[440px] w-full md:h-[600px]">
        {homeSlides.map((item, slideIndex) => (
          <div
            key={item.id + slideIndex}
            className={`absolute inset-0 transition-opacity duration-1000 ${
              slideIndex === index ? "opacity-100" : "opacity-0"
            }`}
          >
            <img
              src={item.image}
              alt={`${item.title} 焦点推荐`}
              title={`${item.title} 影视资讯推荐`}
              className="h-full w-full object-cover object-top"
            />
            <div className="absolute inset-0 bg-gradient-to-t from-primary-950/90 via-primary-950/40 to-primary-950/30"></div>
          </div>
        ))}

        <div className="absolute inset-0 flex items-end">
          <div className="w-full px-4 pb-12 md:px-8 md:pb-16">
            <div key={index} className="max-w-2xl animate-fade-up">
              <span className="inline-flex items-center gap-1 rounded-full bg-accent-500 px-3 py-1 text-xs font-semibold text-background-50">
                <i className="ri-fire-fill"></i>
                {slide.tag}
              </span>
              <h1 className="mt-4 font-heading text-3xl font-bold text-background-50 md:text-5xl">
                {slide.title}
              </h1>
              <p className="mt-3 max-w-xl text-sm leading-relaxed text-background-100/90 md:text-base">
                {slide.subtitle}
              </p>
              <div className="mt-6 flex flex-wrap items-center gap-3">
                <button
                  type="button"
                  onClick={() => navigate(`/detail/${slide.id}`)}
                  className="flex items-center gap-2 whitespace-nowrap rounded-md bg-accent-500 px-5 py-2.5 text-sm font-medium text-background-50 transition-colors hover:bg-accent-600 cursor-pointer"
                >
                  <i className="ri-play-fill text-lg"></i>
                  查看详情
                </button>
                <button
                  type="button"
                  onClick={() => navigate("/movies")}
                  className="whitespace-nowrap rounded-md border border-background-50/50 px-5 py-2.5 text-sm font-medium text-background-50 transition-colors hover:bg-background-50/10 cursor-pointer"
                >
                  浏览电影库
                </button>
              </div>
            </div>
          </div>
        </div>

        <button
          type="button"
          aria-label="上一张"
          onClick={() => goTo(index - 1)}
          className="absolute left-3 top-1/2 hidden h-11 w-11 -translate-y-1/2 items-center justify-center rounded-full border border-background-50/40 bg-primary-950/40 text-background-50 transition-colors hover:bg-primary-950/70 md:flex cursor-pointer"
        >
          <i className="ri-arrow-left-s-line text-2xl"></i>
        </button>
        <button
          type="button"
          aria-label="下一张"
          onClick={() => goTo(index + 1)}
          className="absolute right-3 top-1/2 hidden h-11 w-11 -translate-y-1/2 items-center justify-center rounded-full border border-background-50/40 bg-primary-950/40 text-background-50 transition-colors hover:bg-primary-950/70 md:flex cursor-pointer"
        >
          <i className="ri-arrow-right-s-line text-2xl"></i>
        </button>

        <div className="absolute bottom-4 left-1/2 flex -translate-x-1/2 items-center gap-2">
          {homeSlides.map((item, slideIndex) => (
            <button
              key={item.id + "-dot"}
              type="button"
              aria-label={`切换到第 ${slideIndex + 1} 张`}
              onClick={() => goTo(slideIndex)}
              className={`h-1.5 rounded-full transition-all cursor-pointer ${
                slideIndex === index ? "w-8 bg-accent-500" : "w-3 bg-background-50/50"
              }`}
            ></button>
          ))}
        </div>
      </div>
    </section>
  );
}